import { ArrowBack as ArrowBackIcon, Error as ErrorIcon } from '@mui/icons-material';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import type { Theme } from '@mui/system';
import { useParams } from 'react-router-dom';
import { AdminsPageShell } from '@/components/AdminsPageShell';
import { EmptyState } from '@/components/EmptyState';
import type { MaDeal } from '@/types/GraphqlSchema';
import { ADMINS_MA_DEALS_PATH } from '@/utils/constants';
import { formatDateAsDayMonthYear } from '@/utils/dateFormatter';
import { DealDetailsForm } from './components/DealDetailsForm/DealDetailsForm';
import { DealTransactionsTable } from './components/DealTransactionsTable/DealTransactionsTable';
import { useGetDeal } from './hooks/useGetDeal';

const styles = {
  centered: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '400px',
  },
  form: {
    mt: 4,
    padding: 2,
    border: '1px solid',
    borderRadius: 3,
    borderColor: (theme: Theme) => theme.palette.baseColors.iceBlue.c25,
    backgroundColor: (theme: Theme) => theme.palette.baseColors.cloudGray.c50,
  },
};

type DealDetailsSummaryProps = {
  deal: MaDeal;
};

type DealDetailsContainerProps = {
  dealId: number;
};

const DealDetailsLoadingState = () => (
  <Box sx={styles.centered}>
    <CircularProgress color='secondary' />
  </Box>
);

const DealDetailsErrorState = () => (
  <Box sx={styles.centered}>
    <EmptyState title='Error loading deal details' Icon={ErrorIcon} />
  </Box>
);

const DealDetailsSummary = ({ deal }: DealDetailsSummaryProps) => (
  <Box sx={{ mb: 4 }}>
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
      <Box>
        <Typography variant='subtitle1' fontWeight='bold'>
          Deal Type
        </Typography>
        <Typography variant='body1'>{deal.dealType || '-'}</Typography>
      </Box>
      <Box>
        <Typography variant='subtitle1' fontWeight='bold'>
          Effective Date
        </Typography>
        <Typography variant='body1'>
          {formatDateAsDayMonthYear(deal.effectiveDate) || '-'}
        </Typography>
      </Box>
    </Box>
    <Box sx={{ mt: 4 }}>
      <Typography variant='subtitle1' fontWeight='bold'>
        Comment
      </Typography>
      <Typography variant='body1'>{deal.comment || '-'}</Typography>
    </Box>
  </Box>
);

const DealDetailsContainer = ({ dealId }: DealDetailsContainerProps) => {
  const { deal, loading, error } = useGetDeal(dealId);

  if (loading) {
    return <DealDetailsLoadingState />;
  }

  if (error || !deal || !dealId) {
    return <DealDetailsErrorState />;
  }

  return (
    <Box sx={{ padding: 2 }}>
      <DealDetailsSummary deal={deal} />

      <Box sx={{ mt: 4 }}>
        <Typography variant='subtitle1' fontWeight='bold'>
          Transactions
        </Typography>

        <Box sx={{ mt: 1 }}>
          <DealTransactionsTable dealId={Number(deal.id)} />
        </Box>
      </Box>

      <Box sx={styles.form}>
        <Typography variant='h6' fontFamily={theme => theme.typography.fontFamily}>
          Reported Data
        </Typography>

        <DealDetailsForm deal={deal} />
      </Box>
    </Box>
  );
};

export const MaDealDetails = () => {
  const { id: dealId } = useParams<{ id?: string }>();

  return (
    <AdminsPageShell title='M&A Deal Details'>
      <Button
        variant='text'
        color='secondary'
        href={ADMINS_MA_DEALS_PATH}
        startIcon={<ArrowBackIcon />}
      >
        <Typography variant='body1' sx={{ color: 'secondary' }}>
          Back to reports
        </Typography>
      </Button>

      <DealDetailsContainer dealId={Number(dealId)} />
    </AdminsPageShell>
  );
};
